import { ApolloClient } from '@apollo/client';
import { ExponentialBackoff, handleWhen, retry } from 'cockatiel';

import { TsRestClient } from '@eluve/api-client-provider';
import { ScheduleBackgroundTaskAction } from '@eluve/appointment-tasks';
import { createDefaultLogActionFromCorrelatedLogger } from '@eluve/frontend-machine-utils';
import { CorrelatedLogger } from '@eluve/logger';
import { getMetadataFromFileName, sleep } from '@eluve/utils';

import { defaultPresignedUploadMachine } from './defaultPresignedUrlUploadMachine';
import { AppointmentFileIdbRow, DexieDb } from './files-db';
import { isOwnedOrOrphanedFile } from './isOwnedOrOrphanedFile';
import { isSyncableFile } from './isSyncableFile';
import { createPeriodicFileMachineLogic } from './periodicRemoteFileSync.machine';
import { convertPresignedUrlMachineToCallback } from './presignedUrlUpload.machine';
import { reconcileOrphanCandidates } from './reconcileOrphanCandidates';
import {
  isSlotResolvedAgainstGcs,
  resolveReservedUploadAgainstGcs,
} from './resolveReservedUploadAgainstGcs';
import { getTabId } from './tab-id';
import { NOT_FOUND_ERROR_NAME } from './uploadFile.machine';
import { convertUploadFileMachineToCallback } from './uploadFile.machine';
import { UserFileSystem } from './user-file-system.store';

/**
 * Total OPFS read attempts (1 initial + 4 retries). With the exponential
 * backoff below, exhausting the budget takes ~3s of wall clock — comfortably
 * past any plausible OPFS finalize race while keeping a single sync tick
 * bounded. [ENG-4024]
 */
export const OPFS_READ_MAX_ATTEMPTS = 5;

type ReadOpfsFileArgs = {
  userFileSystem: UserFileSystem;
  appointmentId: string;
  fileName: string;
  log: CorrelatedLogger;
  fileRetrievalDelayInMs?: number;
};

/**
 * Reads an OPFS audio file with bounded retry on NotFoundError to absorb the
 * brief window between a chunk writer's `close()` and the FS observer flush.
 * Throws NotFoundError after the retry budget is exhausted — at that point
 * the file is genuinely missing and the caller should treat it as an orphan. [ENG-4024]
 */
export const readOpfsFileWithFinalizeRetry = async (
  args: ReadOpfsFileArgs,
): Promise<File> => {
  const {
    userFileSystem,
    appointmentId,
    fileName,
    log,
    fileRetrievalDelayInMs,
  } = args;

  if (userFileSystem.type !== 'available') {
    throw new Error('User file system is not available');
  }

  // Cockatiel retry policy for the OPFS finalize race. Only retries when the
  // filesystem reports the file genuinely missing (NotFoundError); any other
  // error (real I/O failure, security exception, etc.) bubbles up immediately.
  // Backoff schedule (initialDelay 200ms, maxDelay 2000ms): 200, 400, 800,
  // 1600 → ~3s total wait across 4 retries. Constructed per-call so the
  // `onRetry` listener below is scoped to this invocation — keeping listener
  // wiring local makes it safe to parallelize callers in the future. [ENG-4024]
  const opfsFinalizeRaceRetryPolicy = retry(
    handleWhen(
      (error) =>
        error instanceof DOMException && error.name === NOT_FOUND_ERROR_NAME,
    ),
    {
      maxAttempts: OPFS_READ_MAX_ATTEMPTS - 1,
      backoff: new ExponentialBackoff({ initialDelay: 200, maxDelay: 2000 }),
    },
  );

  let attemptsUsed = 0;
  // Cockatiel emits this on each *retry* (not the initial attempt), giving us
  // a clean view of how often the finalize race actually fires in prod.
  const onRetryListener = opfsFinalizeRaceRetryPolicy.onRetry(({ attempt }) => {
    log('info', 'OPFS read retry — finalize race', {
      attempt,
      fileName,
      appointmentId,
    });
  });

  try {
    const fileData = await opfsFinalizeRaceRetryPolicy.execute(async () => {
      attemptsUsed += 1;
      const appointmentDir =
        await userFileSystem.appointmentsDir.getDirectoryHandle(appointmentId);

      if (fileRetrievalDelayInMs) {
        await sleep(fileRetrievalDelayInMs);
      }

      const handle = await appointmentDir.getFileHandle(fileName);
      return handle.getFile();
    });

    if (attemptsUsed > 1) {
      // Recovered from the finalize race — count this in Datadog to validate
      // the retry budget tuning.
      log('info', 'OPFS read succeeded after finalize-race retry', {
        attemptsUsed,
        fileName,
        appointmentId,
      });
    }

    return fileData;
  } catch (error) {
    if (error instanceof DOMException && error.name === NOT_FOUND_ERROR_NAME) {
      log(
        'warn',
        'OPFS read exhausted retry budget — escalating to orphan cancel',
        {
          attemptsUsed,
          fileName,
          appointmentId,
        },
      );
    }
    throw error;
  } finally {
    onRetryListener.dispose();
  }
};

type Args = {
  apiClient: TsRestClient;
  apolloClient: ApolloClient<unknown>;
  db: DexieDb;
  userFileSystem: UserFileSystem;
  log: CorrelatedLogger;
  fileRetrievalDelayInMs?: number;
  clientOrigin?: string;
  deviceId: string;
  scheduleBackgroundTask: ScheduleBackgroundTaskAction;
};

/**
 * Escalates a row whose bytes are unrecoverable from OPFS. GCS truth is
 * consulted first (resolveReservedUpload, [ENG-4498]): a missing local file
 * says nothing about whether the upload finalized, so if the backend can
 * resolve the slot from stored bytes the cancel is skipped — the audio was
 * never actually lost. Only when GCS has no object (or the endpoint is
 * unavailable on an older backend) does this fall through to the CANCELLED
 * escalation. Always proceeds even if the API calls fail — the local row
 * tear-down still happens via the machine's CleanupFile state, and the next
 * reconciliation tick will retry. [ENG-4024][ENG-4562]
 */
export const buildCancelOrphanedRow = (args: {
  apiClient: TsRestClient;
  log: CorrelatedLogger;
}) => {
  const { apiClient, log } = args;

  return async ({
    fileName,
    appointmentId,
    tenantId,
    userId,
  }: {
    fileName: string;
    appointmentId: string;
    tenantId: string;
    userId: string;
  }): Promise<void> => {
    const { databaseFileId } = getMetadataFromFileName({ fileName, userId });

    const resolution = await resolveReservedUploadAgainstGcs({
      apiClient,
      log,
      tenantId,
      appointmentId,
      fileId: databaseFileId,
      fileName,
    });
    if (isSlotResolvedAgainstGcs(resolution)) {
      log('info', 'Orphan slot resolved against GCS — skipping cancel', {
        fileName,
        appointmentId,
        fileId: databaseFileId,
        resolution,
      });
      return;
    }

    log('info', 'Escalating orphan to backend cancel (client_fail_fast)', {
      fileName,
      appointmentId,
      fileId: databaseFileId,
      resolution,
    });

    try {
      await apiClient.appointments.cancelOrphanedFile({
        params: { tenantId, appointmentId, fileId: databaseFileId },
        body: { trigger: 'client_fail_fast' },
      });
    } catch (error) {
      log(
        'warn',
        'Orphan cancel API call failed — proceeding with local cleanup',
        {
          fileName,
          appointmentId,
          fileId: databaseFileId,
          error,
        },
      );
    }
  };
};

/**
 * A stale write-ahead backup whose session never degraded is redundant: post-
 * parity the chunks are written incrementally and already carry the full
 * audio, so uploading the backup would duplicate the segment. Only a degraded
 * backup (chunking failed mid-segment — the backup is the only complete
 * audio) is worth uploading. Rows without the marker (old data, v1, pcm
 * batch) are untouched and upload exactly as today.
 */
const isDiscardableWriteAheadBackup = (row: AppointmentFileIdbRow): boolean =>
  row.isWriteAheadBackup === true && !row.wasDegraded;

/**
 * Builds the crash-recovery row disposition shared by the sync machine: the
 * page-load sweep of this tab's stale 'recording' rows, and the per-tick file
 * selection (own files + dead-tab orphan claim). Extracted from the machine
 * wiring so the real selection/sweep logic is directly testable.
 */
export const createFileSyncSelection = ({
  db,
  userFileSystem,
  log,
}: {
  db: DexieDb;
  userFileSystem: UserFileSystem;
  log: CorrelatedLogger;
}) => {
  // Same dirPath derivation as the post-upload cleanupFile below: files live
  // directly under appointmentsDir/<appointmentId>/<fileName>.
  const deleteOpfsFile = async (appointmentId: string, fileName: string) => {
    if (userFileSystem.type !== 'available') return;
    const appointmentDir =
      await userFileSystem.appointmentsDir.getDirectoryHandle(appointmentId);
    await appointmentDir.removeEntry(fileName);
  };

  // Best-effort teardown of a redundant backup (Dexie row + OPFS bytes). A
  // failure leaves the row in place for the next tick to retry.
  const discardWriteAheadBackup = async (row: AppointmentFileIdbRow) => {
    log('info', 'Discarding redundant write-ahead backup', {
      appointmentId: row.appointmentId,
      fileName: row.id,
    });
    await db.appointmentFiles.delete(row.id);
    await deleteOpfsFile(row.appointmentId, row.id).catch((error) => {
      log('warn', 'Failed to remove write-ahead backup OPFS file', {
        appointmentId: row.appointmentId,
        fileName: row.id,
        error,
      });
    });
  };

  // Page-load sweep: rows this tab left in writeState 'recording' cannot
  // still be written (the writing worker died with the previous page), so
  // flip them to 'final' before the first sync tick. Without this, a crash or
  // reload during recording would strand its in-progress chunk forever, since
  // the selection filter below skips live-tab 'recording' rows. Un-degraded
  // write-ahead backups are the exception: their audio is already covered by
  // the chunks, so they are deleted instead of flipped-to-final.
  let staleRecordingSweepDone = false;
  const sweepStaleRecordingRows = async () => {
    if (staleRecordingSweepDone) return;
    staleRecordingSweepDone = true;
    const currentTabId = getTabId();
    try {
      const staleRows = await db.appointmentFiles
        .filter(
          (row) =>
            row.writeState === 'recording' && row.ownedByTabId === currentTabId,
        )
        .toArray();
      let flipped = 0;
      for (const row of staleRows) {
        if (isDiscardableWriteAheadBackup(row)) {
          await discardWriteAheadBackup(row).catch((error) => {
            log('warn', 'Failed to discard stale write-ahead backup', {
              appointmentId: row.appointmentId,
              fileName: row.id,
              error,
            });
          });
          continue;
        }
        await db.appointmentFiles.update(row.id, {
          writeState: 'final' as const,
        });
        flipped += 1;
      }
      if (flipped) {
        log('info', 'Flipped stale recording rows to final on page load', {
          flipped,
        });
      }
    } catch {
      // Best-effort, mirrors the pre-refactor `.catch(() => 0)`. The lazy
      // backstop in getFileToSync will not retry (once-flag), but the next
      // page load will.
    }
  };

  const getFileToSync = async () => {
    await sweepStaleRecordingRows();

    const syncableFiles = await db.appointmentFiles
      .orderBy('lastUploadAttemptedAt')
      .filter(isSyncableFile)
      .toArray();

    // One-off self-heal: a single user has a year-old (2025-03-08)
    // orphan file persisted in OPFS under a different tenant than the
    // appointment actually lives in. The composite FK on appointment_files
    // means uploads will never succeed for this (tenantId, appointmentId)
    // pair. Tear down local state on sight so the periodic scan and the
    // drift reconciler both stop picking it up. Safe to remove once it
    // stops appearing in production logs. [ENG-4253]
    const STUCK_ORPHAN_APPOINTMENT_ID = '8a382356-9052-4b53-968b-aecc4f788657';
    if (
      syncableFiles.some((f) => f.appointmentId === STUCK_ORPHAN_APPOINTMENT_ID)
    ) {
      log('info', 'Cleaning up known stuck-orphan appointment files', {
        appointmentId: STUCK_ORPHAN_APPOINTMENT_ID,
      });
      await db.appointmentFiles
        .where('appointmentId')
        .equals(STUCK_ORPHAN_APPOINTMENT_ID)
        .delete()
        .catch(() => undefined);
      if (userFileSystem.type === 'available') {
        await userFileSystem.appointmentsDir
          .removeEntry(STUCK_ORPHAN_APPOINTMENT_ID, { recursive: true })
          .catch(() => undefined);
      }
      return null;
    }

    // Filter by tab ownership: own files first, dead-tab files only [ENG-3289]
    const currentTab = getTabId();
    let file = null;
    for (const candidate of syncableFiles) {
      // The worker in this tab is still writing this chunk — uploading
      // now would ship partial bytes. It becomes eligible when the
      // chunk-finalized handler flips it to 'final' (or, after a crash or
      // reload, when the page-load sweep does). 'recording' rows owned by
      // a DEAD tab fall through to the normal orphan claim below — a
      // partial chunk from a crashed session is still worth uploading.
      if (
        candidate.writeState === 'recording' &&
        candidate.ownedByTabId === currentTab
      ) {
        continue;
      }
      if (!(await isOwnedOrOrphanedFile(candidate))) {
        continue;
      }
      // Dead-tab (or leftover) un-degraded backup claimed by the orphan
      // recovery: its audio is already covered by the chunks — discard
      // instead of uploading a duplicate segment.
      if (isDiscardableWriteAheadBackup(candidate)) {
        await discardWriteAheadBackup(candidate).catch((error) => {
          log('warn', 'Failed to discard orphaned write-ahead backup', {
            appointmentId: candidate.appointmentId,
            fileName: candidate.id,
            error,
          });
        });
        continue;
      }
      file = candidate;
      break;
    }

    if (file) {
      if (file.isWriteAheadBackup && file.wasDegraded) {
        // Degrade-then-crash residual: this raw backup is the segment's only
        // complete audio and is being recovered by the sweep. Warn-level on
        // purpose — this is the monitored case.
        log('warn', 'Uploading degraded write-ahead backup', {
          appointmentId: file.appointmentId,
          fileName: file.id,
        });
      }
      const newAttemptCount = file.attemptCount + 1;
      await db.appointmentFiles.update(file.id, {
        lastUploadAttemptedAt: new Date().toISOString(),
        attemptCount: newAttemptCount,
        // Claim ownership so other tabs back off [ENG-3289]
        ownedByTabId: currentTab,
        // Selecting a 'recording' row means its owning tab is dead (live
        // own-tab rows were skipped above), so no one is still writing it.
        // Finalize on claim — otherwise a failed upload attempt would leave
        // the row 'recording' + owned by this live tab, and the filter
        // above would strand it until the next page load.
        ...(file.writeState === 'recording'
          ? { writeState: 'final' as const }
          : {}),
      });
    }

    return file;
  };

  return { sweepStaleRecordingRows, getFileToSync };
};

export const makeDefaultRemoteFileSyncMachineLogic = (args: Args) => {
  const {
    apiClient,
    apolloClient,
    db,
    log,
    userFileSystem,
    fileRetrievalDelayInMs,
    clientOrigin,
    deviceId,
    scheduleBackgroundTask,
  } = args;

  const { sweepStaleRecordingRows, getFileToSync } = createFileSyncSelection({
    db,
    userFileSystem,
    log,
  });

  // Run the sweep NOW, not lazily on the first sync tick. A recording started
  // between page load and that first tick registers live 'recording' rows a
  // deferred sweep would wrongly flip to 'final' — uploading partial files
  // mid-recording. At factory creation no recording can have started in this
  // tab, so everything the sweep sees is genuinely stale. The lazy call in
  // getFileToSync stays as a backstop (guarded by the once-flag).
  void sweepStaleRecordingRows();

  const logic = createPeriodicFileMachineLogic({
    actions: {
      log: createDefaultLogActionFromCorrelatedLogger(log),
    },
    actors: {
      reconcileOrphans: ({ tenantId, userId }) =>
        reconcileOrphanCandidates({
          apiClient,
          db,
          userFileSystem,
          log,
          tenantId,
          userId,
          deviceId,
        }),
      updateLastAttemptedAt: async ({
        id,
        attemptCount,
      }: {
        id: string;
        attemptCount: number;
      }) => {
        await db.appointmentFiles.update(id, {
          lastUploadAttemptedAt: new Date().toISOString(),
          attemptCount,
        });
      },
      getFileToSync,
      uploadFile: convertUploadFileMachineToCallback({
        logger: log,
        getLogAttributes: (input) => ({
          appointmentId: input.appointmentId,
        }),
        getFile: async ({ appointmentId, fileName }) => {
          log('info', 'Attempting to read file from user file system', {
            appointmentId,
            fileName,
          });

          const fileData = await readOpfsFileWithFinalizeRetry({
            userFileSystem,
            appointmentId,
            fileName,
            log,
            fileRetrievalDelayInMs,
          });

          log('info', 'File read from user file system', {
            appointmentId,
            fileName,
            fileSize: fileData.size,
          });

          return fileData;
        },
        cancelOrphanedRow: buildCancelOrphanedRow({ apiClient, log }),
        uploadFile: convertPresignedUrlMachineToCallback(
          defaultPresignedUploadMachine({
            apiClient,
            log,
            apolloClient,
            clientOrigin,
            deviceId,
            scheduleBackgroundTask,
          }),
        ),
        cleanupFile: async ({ appointmentId, fileId }) => {
          log('info', 'Removing file', { appointmentId, fileId });
          if (userFileSystem.type === 'available') {
            await db.appointmentFiles.delete(fileId);
            const appointmentDir =
              await userFileSystem.appointmentsDir.getDirectoryHandle(
                appointmentId,
              );

            await appointmentDir.removeEntry(fileId).catch((e) => {
              log('warn', 'Failed to remove file entry', { fileId, error: e });
            });

            log('info', 'Finished cleaning up file', { fileId });
          } else {
            throw new Error('User file system is not available');
          }
        },
      }),
    },
  });

  return logic;
};
